// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';

import { convertInchesToImperialString } from 'helpers/convertInchesToImperialString';
import {
  DimensionLabel,
  type DimensionLabelOpts,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/DimensionLabel';
import { labelTheming } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/labelTheming';
import { MeasurementGeometry } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/MeasurementGeometry';

export type MeasurementLabelOpts = {
  start: THREE.Vector3;
  end: THREE.Vector3;
} & DimensionLabelOpts;

export class MeasurementLabel extends DimensionLabel {
  declare geometry: MeasurementGeometry;

  constructor({ start, end, ...dimensionLabelOpts }: MeasurementLabelOpts) {
    super(
      (name) => new MeasurementGeometry(name, { start, end, renderOrder: labelTheming.renderOrder.labelOnBend }),
      dimensionLabelOpts,
    );
  }

  /**
   * Measurement labels use a single leader line going to the midpoint on perspective cameras.
   * In orthographic (flat) views, both endpoints are used as anchors, producing standoff lines
   * connected by a connector line. The side the connector is placed on is controlled by
   * {@link DimensionLabelOpts.directionPreference}.
   *
   * @param camera The camera to use for determining the anchor point.
   */
  getAnchorWorldPoints(camera: THREE.Camera): THREE.Vector3[] {
    this.geometry.toggleLinesForCamera(camera);
    if (camera instanceof THREE.PerspectiveCamera) {
      return [this.geometry.localToWorld(this.geometry.perspectiveLine.midpointLocal.clone())];
    }
    return [
      this.geometry.localToWorld(this.geometry.orthoLine.startLocal.clone()),
      this.geometry.localToWorld(this.geometry.orthoLine.endLocal.clone()),
    ];
  }

  formatValue(value: number) {
    return convertInchesToImperialString(value * 12);
  }
}
